// SGNL-99 — singleton popover that shows tag-verdict rationale when a
// chip is clicked. Lazy-fetches /api/opportunities/{id}/detail (cached
// in module state) and locates the matching tag.
//
// Why singleton: a Deal Explorer with 500 rows × 6 chips each would
// otherwise want 3000 popover elements. One shared element repositioned
// on demand is the standard approach.
//
// Pure-vanilla DOM. Closes on Esc, outside click, or another chip click.

import type { OpportunityTagOut } from "../types";
import { parseOppHash, scrollToCitation } from "../views/opp_detail";
import { esc } from "./format";
import { getOpportunityDetail } from "./opportunity_detail_cache";

// `/detail` cache is shared with `tag_rationales_panel.ts` via
// `opportunity_detail_cache.ts` (PR #57 review F1). Pre-fix each
// module had its own Map and a click-popover-then-open-panel sequence
// fired `/detail` twice for the same opp.

let popoverEl: HTMLDivElement | null = null;
let outsideClickHandler: ((ev: MouseEvent) => void) | null = null;
let escHandler: ((ev: KeyboardEvent) => void) | null = null;

function ensurePopoverEl(): HTMLDivElement {
  if (popoverEl) return popoverEl;
  const el = document.createElement("div");
  el.setAttribute("data-testid", "tag-popover");
  el.setAttribute("role", "dialog");
  el.style.cssText = [
    "position:absolute",
    "z-index:1000",
    "max-width:340px",
    "padding:10px 12px",
    "background:var(--bg-card, #fff)",
    "color:var(--ink, #0f172a)",
    "border:1px solid var(--line, #e5e7eb)",
    "border-radius:6px",
    "box-shadow:0 4px 12px rgba(0,0,0,0.15)",
    "font-size:13px",
    "line-height:1.4",
    "display:none",
  ].join(";");
  // Close-button delegated listener — attached EXACTLY ONCE on the
  // singleton element, not per `openTagPopover()` call. Pre-fix the
  // listener was appended on every open with `{ once: false }`,
  // which accumulated unbounded handlers across the SPA session
  // (memory leak + N redundant `closeTagPopover()` invocations per
  // click inside the popover). Review F1 / AR Q1 / Sec #2 on PR #56.
  el.addEventListener("click", (ev) => {
    const tgt = ev.target as HTMLElement;
    if (tgt.dataset.action === "close-popover") {
      ev.stopPropagation();
      closeTagPopover();
    }
  });
  document.body.appendChild(el);
  popoverEl = el;
  return el;
}

export function closeTagPopover(): void {
  if (popoverEl) {
    popoverEl.style.display = "none";
  }
  if (outsideClickHandler) {
    document.removeEventListener("click", outsideClickHandler, true);
    outsideClickHandler = null;
  }
  if (escHandler) {
    document.removeEventListener("keydown", escHandler);
    escHandler = null;
  }
}

function renderCitationsBlock(tag: OpportunityTagOut, oppId: string): string {
  // Empty list AND a verdict that SHOULD have had citations → tell the
  // operator why nothing's clickable (pre-v1.2 classification). Empty
  // list on a 'no' verdict is silently fine — those don't carry
  // citations by design.
  const citations = tag.citations ?? [];
  if (citations.length === 0) {
    if (tag.verdict === "yes" || tag.verdict === "maybe") {
      return (
        `<div class="muted" style="margin-top:8px; font-size:11px;">` +
        `Older classification — no structured citations.` +
        `</div>`
      );
    }
    return "";
  }
  // code-reviewer F-02 + architect ARCH-H1 on PR #70: removed the
  // identity-map _SOURCE_TYPE_LABEL — interpolate source_type
  // directly. Same fix applied in renderTagCitationsInline.
  const items = citations
    .map((c) => {
      const snippet = (c.snippet ?? "").trim();
      const snippetMarkup = snippet
        ? ` <span class="cite-snippet">"${esc(snippet)}"</span>`
        : "";
      return (
        `<li>` +
        `<a class="cite-link" href="#opp/${esc(oppId)}?cite=${esc(c.source_type)}:${esc(c.source_id)}" ` +
        `data-cite-source-type="${esc(c.source_type)}" ` +
        `data-cite-source-id="${esc(c.source_id)}">` +
        `[${esc(c.source_type)}]${snippetMarkup}` +
        `</a>` +
        `</li>`
      );
    })
    .join("");
  return (
    `<div class="cite-block" style="margin-top:8px;">` +
    `<div class="muted" style="font-size:11px; margin-bottom:4px;">` +
    `Cited source rows (click to jump):` +
    `</div>` +
    `<ul class="cite-list" data-testid="tag-citations">${items}</ul>` +
    `</div>`
  );
}

function renderPopoverContent(tag: OpportunityTagOut | null, oppId: string, tagKey: string): string {
  if (!tag) {
    return (
      `<div><strong>${esc(tagKey)}</strong></div>` +
      `<div class="muted" style="margin-top:6px;">No verdict found for opp ${esc(oppId)}.</div>`
    );
  }
  const conf = (Number(tag.confidence) * 100).toFixed(0);
  const rationale = tag.rationale ?? "(no rationale recorded)";
  return (
    `<div style="display:flex; justify-content:space-between; gap:8px; align-items:baseline;">` +
    `<strong>${esc(tag.label)}</strong>` +
    `<span class="muted" style="font-size:11px;">${esc(tag.category)}</span>` +
    `</div>` +
    `<div style="margin-top:4px;">verdict: <strong>${esc(tag.verdict)}</strong> · ` +
    `confidence: <strong>${esc(conf)}%</strong></div>` +
    `<div style="margin-top:8px; white-space:pre-wrap;">${esc(rationale)}</div>` +
    renderCitationsBlock(tag, oppId) +
    `<button type="button" class="btn-icon-close" data-action="close-popover" ` +
    `style="position:absolute; top:4px; right:6px;" ` +
    `aria-label="Close popover">` +
    `<svg width="14" height="14" viewBox="0 0 14 14" fill="none" aria-hidden="true">` +
    `<path d="M1 1l12 12M13 1L1 13" stroke="currentColor" stroke-width="2" stroke-linecap="round"/>` +
    `</svg></button>`
  );
}

function positionAnchor(el: HTMLElement, anchor: HTMLElement): void {
  const rect = anchor.getBoundingClientRect();
  // Anchor below the chip; if it would overflow the viewport bottom,
  // flip above. Add scrollY since position is absolute relative to
  // the document, not the viewport.
  const top = rect.bottom + window.scrollY + 4;
  const left = rect.left + window.scrollX;
  el.style.top = `${top}px`;
  el.style.left = `${left}px`;
  el.style.display = "block";
  // Flip-up if overflow.
  const popRect = el.getBoundingClientRect();
  if (popRect.bottom > window.innerHeight) {
    el.style.top = `${rect.top + window.scrollY - popRect.height - 4}px`;
  }
}

export async function openTagPopover(anchor: HTMLElement, oppId: string, tagKey: string): Promise<void> {
  const el = ensurePopoverEl();
  el.innerHTML = `<div class="muted">Loading…</div>`;
  positionAnchor(el, anchor);

  // Outside click + Esc handlers — install on first open, removed on close.
  outsideClickHandler = (ev: MouseEvent) => {
    const target = ev.target as Node;
    if (el.contains(target)) return;
    // If the click is on another tag chip, the chip's own handler will
    // re-open the popover; we just close here.
    closeTagPopover();
  };
  document.addEventListener("click", outsideClickHandler, true);
  escHandler = (ev: KeyboardEvent) => {
    if (ev.key === "Escape") closeTagPopover();
  };
  document.addEventListener("keydown", escHandler);

  // (Close-button click handler attached once in ensurePopoverEl —
  // do NOT re-attach here.)

  // Shared lazy-fetch cache (handles inflight-share + on-error eviction).
  try {
    const detail = await getOpportunityDetail(oppId);
    const tag = (detail.tags ?? []).find((t) => t.tag_key === tagKey) ?? null;
    el.innerHTML = renderPopoverContent(tag, oppId, tagKey);
    // SGNL-128 — wire citation click-through. If the operator is
    // already on the Opp Detail page for this opp, scroll in-place
    // without a hash change (smoother UX, no full re-render). If
    // they're elsewhere, the anchor's href takes them via the hash
    // router; `parseOppCite` in opp_detail.ts handles the deep-link
    // on mount.
    for (const link of el.querySelectorAll<HTMLAnchorElement>("a.cite-link")) {
      link.addEventListener("click", (ev) => {
        const sourceType = link.dataset.citeSourceType;
        const sourceId = link.dataset.citeSourceId;
        if (!sourceType || !sourceId) return;
        // In-page scroll only when we're already viewing this opp.
        // Compare against the current hash so a hash route like
        // `#opp/006xxx` matches even if oppId looked different (e.g.,
        // case-folded path segment). parseOppHash gives us the
        // canonical id from the current URL.
        const currentOppId = parseOppHash(window.location.hash);
        if (currentOppId !== oppId) {
          // Hash change → main.ts re-renders the detail page; on-mount
          // scroll happens via parseOppCite. Let the default
          // navigation happen.
          return;
        }
        // code-reviewer F-01 on PR #70: preventDefault FIRST inside
        // the same-opp branch. If scrollToCitation returns false (cited
        // row not in DOM), the default <a href> click would change the
        // hash, trigger a router re-mount, and the on-mount cite path
        // would also no-op silently — visible flicker for no gain.
        // Consume the click unconditionally; popover stays open so the
        // operator can click the next citation.
        ev.preventDefault();
        scrollToCitation(document.body, {
          source_type: sourceType,
          source_id: sourceId,
        });
      });
    }
  } catch (err) {
    const msg = err instanceof Error ? err.message : String(err);
    el.innerHTML =
      `<div><strong>${esc(tagKey)}</strong></div>` +
      `<div class="muted" style="margin-top:6px;">Failed to load: ${esc(msg)}</div>`;
  }
}

/** Test-only: wipe popover element. Cache lives in
 * `opportunity_detail_cache.ts`; clear that separately if needed. */
export function _clearTagPopoverCache(): void {
  closeTagPopover();
  if (popoverEl) {
    popoverEl.remove();
    popoverEl = null;
  }
}
